import React, {useState} from 'react';
import {View, TouchableOpacity, StyleSheet, TextInput} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import {FontSizes, isPhone} from '@/Utils/Dimensions';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import useGetStudentClassFeedbackForm from '@/Hooks/WebinarApi/useGetStudentClassFeedbackForm';
import useSubmitStudentClassFeedback from '@/Hooks/WebinarApi/useSubmitStudentClassFeedback';

const IMPROVEMENT_OPTIONS = [
  {id: '1', label: 'Coach explanation'},
  {id: '2', label: 'Engagement / interaction'},
  {id: '3', label: 'Content difficulty'},
  {id: '4', label: 'Pace of the class'},
  {id: '5', label: 'Technical issues'},
  {id: '6', label: 'Schedule / timing'},
  {id: '7', label: 'Something else'},
];

type Props = {
  visible: boolean;
  onClose: () => void;
  topic: string;
  coachName: string;
  webinarId: number;
  classType?: string;
  userData;
  onSubmit: (payload: any) => void;
};

export default function ClassReviewModal({
  visible,
  onClose,
  topic,
  coachName,
  webinarId,
  classType,
  userData,
  onSubmit,
}: Props) {
  const [rating, setRating] = useState<number | null>(null);
  const [reasonIds, setReasonIds] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  const {data: feedbackForm, isLoading} = useGetStudentClassFeedbackForm(classType);

  const improvementStep = feedbackForm?.data?.steps?.find(
    step => step.step === 'IMPROVEMENTS',
  );

  const improvementOptions = improvementStep?.reasons ?? IMPROVEMENT_OPTIONS;

  const submitFeedbackMutation = useSubmitStudentClassFeedback(response => {
    // show thank you screen / toast
  });

  const isSomethingElseSelected =
    !!rating &&
    rating < 5 &&
    improvementOptions.some(
      option =>
        option?.label?.toLowerCase()?.includes('other') &&
        reasonIds?.includes(String(option?.id)),
    );

  const isFiveStar = rating === 5;

  const toggleReason = (id: string) => {
    setReasonIds(prev =>
      prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id],
    );
  };

  const resetState = () => {
    setRating(null);
    setReasonIds([]);
    setComment('');
  };

  const handleSubmit = () => {
    if (!rating) return;

    submitFeedbackMutation.mutate(
      {
        student_id: userData?.user_id,
        webinar_id: webinarId,
        rating: rating!,
        reason_ids: isFiveStar ? [] : reasonIds?.map(id => Number(id)),
        comment: isSomethingElseSelected ? comment?.trim() : '',
        source: 'web',
      },
      {
        onSuccess: () => {
          resetState();
          onClose();
          onSubmit({
            student_id: userData?.user_id,
            webinar_id: webinarId,
            rating: rating!,
            reason_ids: isFiveStar ? [] : reasonIds?.map(id => Number(id)),
            comment: isSomethingElseSelected ? comment?.trim() : '',
            source: 'web',
          });
        },
      },
    );
  };

  const disableSubmit =
    !rating ||
    (rating < 5 &&
      (reasonIds?.length === 0 ||
        (isSomethingElseSelected && !comment?.trim())));

  const handleRatingChange = (value: number) => {
    setRating(value);
    if (value === 5) {
      setReasonIds([]);
      setComment('');
    }
  };

  return (
    <SlideModal
      visible={visible}
      onClose={() => {
        resetState();
        onClose();
      }}
      type="center"
      customStyle={{width: isPhone ? '90%' : 380}}
      customBodyStyle={{paddingTop: 0}}>
      <View style={styles.card}>
        <CCText
          fontSize={FontSizes['24']}
          style={styles.title}
          color={CCColors?.TextColor?.Primary}>
          Rate your class experience
        </CCText>
        <CCText
          fontSize={FontSizes['14']}
          color={CCColors?.TextColor?.Primary}
          style={styles.subtitle}>
          {topic} · Coach {coachName}
        </CCText>
        <View style={styles.starRow}>
          {[1, 2, 3, 4, 5].map(star => {
            const active = rating && star <= rating;
            return (
              <TouchableOpacity
                key={star}
                activeOpacity={0.7}
                onPress={() => handleRatingChange(star)}
                style={styles.starButton}>
                <MaterialIcons
                  name={active ? 'star' : 'star-border'}
                  size={FontSizes['36']}
                  color={
                    active ? CCColors.Rewards.TextRed : CCColors.Grey.Shade200
                  }
                />
              </TouchableOpacity>
            );
          })}
        </View>
        {rating && rating < 5 && (
          <>
            <View style={styles.section}>
              <CCText style={styles.sectionTitle}>What can we improve?</CCText>

              <View style={styles.chipWrap}>
                {improvementOptions.map(item => {
                  const id = String(item?.id);
                  const selected = reasonIds?.includes(id);
                  return (
                    <TouchableOpacity
                      key={id}
                      onPress={() => toggleReason(id)}
                      activeOpacity={0.85}
                      style={[styles.chip, selected && styles.chipActive]}>
                      {selected && (
                        <MaterialIcons
                          name="check"
                          size={FontSizes['14']}
                          color={CCColors?.Rewards?.TextRed}
                          style={{marginRight: 6}}
                        />
                      )}
                      <CCText
                        fontSize={FontSizes['13']}
                        color={CCColors?.TextColor?.Dark}
                        style={selected && styles.chipTextActive}>
                        {item?.label}
                      </CCText>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
            <View style={styles.commentBox}>
              <TextInput
                value={comment}
                onChangeText={setComment}
                placeholder="Additional Comment (optional)"
                placeholderTextColor={CCColors?.TextColor?.Grey}
                multiline
                maxLength={250}
                style={styles.textInput}
              />
              <CCText style={styles.charCount}>{comment?.length}/250</CCText>
            </View>
          </>
        )}
        <View style={styles.buttonWrap}>
          <CCButton
            disabled={disableSubmit || submitFeedbackMutation?.isLoading}
            style={styles.submitBtn}
            onPress={handleSubmit}
            titleStyle={{fontSize: FontSizes[16]}}>
            Submit
          </CCButton>
        </View>
      </View>
    </SlideModal>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    backgroundColor: CCColors?.White,
    paddingHorizontal: isPhone ? 0 : 20,
    justifyContent: isPhone ? 'flex-start' : 'center',
    borderRadius: 10,
  },
  headerTitle: {
    fontWeight: '700',
  },
  title: {
    fontWeight: '700',
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    marginTop: 4,
  },
  starRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 20,
  },
  starButton: {
    paddingHorizontal: 6,
  },
  section: {
    marginTop: 24,
  },
  sectionTitle: {
    fontSize: FontSizes['16'],
    fontWeight: '600',
    marginBottom: 12,
  },
  chipWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: CCColors?.OnboardingGrey,
    backgroundColor: CCColors?.Grey?.BG100,
  },
  chipActive: {
    backgroundColor: updateColorWithOpacity(CCColors?.Rewards?.TextRed, 0.2),
    borderColor: CCColors?.Rewards?.TextRed,
  },
  chipTextActive: {
    color: CCColors?.Rewards?.TextRed,
    fontWeight: '600',
  },
  commentBox: {
    marginTop: 14,
  },
  textInput: {
    minHeight: 80,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: CCColors?.OnboardingGrey,
    padding: 12,
    fontSize: FontSizes['14'],
    color: CCColors.TextColor.Dark,
    textAlignVertical: 'top',
    backgroundColor: CCColors?.White,
  },
  charCount: {
    fontSize: FontSizes['10'],
    color: CCColors.TextColor.Grey,
    alignSelf: 'flex-end',
    marginTop: 4,
  },
  buttonWrap: {
    alignItems: 'center',
    marginTop: 14,
  },
  submitBtn: {
    width: isPhone ? '100%' : 200,
  },
});
